import {useEffect, useState} from "react";
import {useSearchParams} from "react-router";
import {
    updateUrlOnDateSwitchChange,
    updateUrlParams
} from "../../../utils";
import {FieldMultiSelect} from "@kroschke/react-ui";
import {
    convertPlacementProcessDocumentExistence,
    convertPlacementProcessOrigin,
    convertPlacementProcessStatus
} from "../../../api";
import {
    PlacementProcessDocumentExistenceFilterDto,
    PlacementProcessOriginDto,
    PlacementProcessStatusDto
} from "../../../api/sls";
import {pageQueryStore, slsCustomerStore, slsOverviewStore, SlsUserItem} from "../../../store";
import {
    CustomerFilterAutoComplete,
    FieldDateSwitch2, FieldDateSwitchValue
} from "../../ui";

const statusDropDownOptions = Object.keys(PlacementProcessStatusDto).map(e => ({
    value: e,
    label: convertPlacementProcessStatus(e as PlacementProcessStatusDto)
}));

const originDropDownOptions = Object.keys(PlacementProcessOriginDto).map(e => ({
    value: e,
    label: convertPlacementProcessOrigin(e as PlacementProcessOriginDto)
}));

const documentCountDropDownOptions = Object.keys(PlacementProcessDocumentExistenceFilterDto).map(e => ({
    value: e,
    label: convertPlacementProcessDocumentExistence(e as PlacementProcessDocumentExistenceFilterDto)
}));

interface FilterbarProps {
    users: SlsUserItem[]
}

function SlsItemFilterbar({users}: FilterbarProps) {
    const [searchParams, setSearchParams] = useSearchParams()

    const toS = searchParams.get('toAbsolute') || searchParams.get('toRelative') || undefined
    const fromS = searchParams.get('fromAbsolute') || searchParams.get('fromRelative') || undefined
    const query = searchParams.get('q') || undefined
    const [customerId, setCustomerId] = useState<string | undefined>(searchParams.get('customerId') || undefined)
    const [usernames, setUserIds] = useState<string[]>(searchParams.getAll('usernames') || [])
    const [origins, setOrigins] = useState<string[]>(searchParams.getAll('origins') || [])
    const [status, setStatus] = useState<string[]>(searchParams.getAll('status') || [])
    const [documentExistence, setDocumentExistence] = useState<string[]>(searchParams.getAll('documentExistence') || undefined)
    const [from, setFrom] = useState<FieldDateSwitchValue | undefined>(fromS ? {
        absolute: searchParams.get('fromAbsolute') || undefined,
        relative: searchParams.get('fromRelative') || undefined,
    } : undefined)
    const [to, setTo] = useState<FieldDateSwitchValue | undefined>(toS ? {
        absolute: searchParams.get('toAbsolute') || undefined,
        relative: searchParams.get('toRelative') || undefined,
    } : undefined)


    useEffect(() => {

        const toS = searchParams.get('toAbsolute') || searchParams.get('toRelative') || undefined
        const fromS = searchParams.get('fromAbsolute') || searchParams.get('fromRelative') || undefined
        setFrom(fromS ? {
            absolute: searchParams.get('fromAbsolute') || undefined,
            relative: searchParams.get('fromRelative') || undefined,
        } : undefined)
        setTo(toS ? {
            absolute: searchParams.get('toAbsolute') || undefined,
            relative: searchParams.get('toRelative') || undefined,
        } : undefined)

        setCustomerId(searchParams.get('customerId') || undefined)
        setUserIds(searchParams.getAll('usernames') || [])
        setOrigins(slsOverviewStore.origins ?? (searchParams.getAll('origins') || []))
        setStatus(slsOverviewStore.status ?? (searchParams.getAll('status') || []))
        setDocumentExistence(slsOverviewStore.documentExistence ?? (searchParams.getAll('documentExistence') || []))
    }, [searchParams, setFrom, setTo])

    const isDefaultFilter =
        !Boolean(query) &&
            !Boolean(customerId) &&
            origins.length === 0 &&
            usernames.length === 0 &&
            status.length === 1 && status.includes(PlacementProcessStatusDto.ACTIVE) &&
            documentExistence.length === 1 && documentExistence.includes(PlacementProcessDocumentExistenceFilterDto.EXISTS) &&
            from?.relative === 'PT48H'

    const userOptions = users.map(u => ({value: u.username, label: u.label}))

    return <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        zoom: '85%',
        zIndex: 1
    }}>
        <div style={{
            minWidth: '15em',
            maxWidth: '25em',
            marginRight: '1em'
        }}>
            <CustomerFilterAutoComplete
                items={slsCustomerStore.items}
                search={slsCustomerStore.search}
                param={"customerId"}/>
        </div>
        <div style={{
            minWidth: '2em',
            maxWidth: '7.5em',
            marginRight: '1em'
        }}>
            <FieldDateSwitch2
                label={"Von"}
                value={from}
                onChange={v => {
                    updateUrlOnDateSwitchChange(v, 'from', searchParams, setSearchParams)
                    setFrom(v)
                }}
            />
        </div>
        <div style={{
            minWidth: '2em',
            maxWidth: '7.5em',
            marginRight: '1em'
        }}>
            <FieldDateSwitch2
                label={"Bis"}
                value={to}
                onChange={v => {
                    updateUrlOnDateSwitchChange(v, 'to', searchParams, setSearchParams)
                    setTo(v)
                }}
            />
        </div>
        <div style={{
            minWidth: '15em',
            maxWidth: '20em',
            marginRight: '1em'
        }}>
            <FieldMultiSelect
                label={"Quelle"}
                options={originDropDownOptions}
                required={false}
                value={origins}
                onChange={value => {
                    updateUrlParams({val: [{param: 'origins', value}], searchParams, setSearchParams})
                    setOrigins(value)
                }}
            />
        </div>
        <div style={{
            minWidth: '15em',
            maxWidth: '25em',
            marginRight: '1em'
        }}>
            <FieldMultiSelect
                label={"Benutzer"}
                options={userOptions}
                required={false}
                value={usernames}
                onChange={value => {
                    updateUrlParams({val: [{param: 'usernames', value}], searchParams, setSearchParams})
                    setUserIds(value)
                }}
            />
        </div>
        <div style={{
            minWidth: '12em',
            maxWidth: '20em',
            marginRight: '1em'
        }}>
            <FieldMultiSelect
                label={"Status"}
                options={statusDropDownOptions}
                required={false}
                value={status}
                onChange={value => {
                    updateUrlParams({val: [{param: 'status', value}], searchParams, setSearchParams})
                    setStatus(value)
                }}
            />
        </div>
        <div style={{
            minWidth: '12em',
            maxWidth: '20em',
            marginRight: '1em'
        }}>
            <FieldMultiSelect
                label={"Dokumente"}
                options={documentCountDropDownOptions}
                required={false}
                value={documentExistence}
                onChange={value => {
                    updateUrlParams({val: [{param: 'documentExistence', value}], searchParams, setSearchParams})
                    setDocumentExistence(value)
                }}
            />
        </div>
        <div style={{
            minWidth: '10em',
            maxWidth: '20em'
        }}>
            <button
                className={'button is-primary is-rounded'}
                disabled={isDefaultFilter}
                onClick={() => {
                    setCustomerId(undefined)
                    setUserIds([])
                    setOrigins([])
                    setStatus([])
                    setDocumentExistence([])
                    searchParams.delete('q')
                    searchParams.delete('customerId')
                    searchParams.delete('usernames')
                    searchParams.delete('origins')
                    searchParams.set('status', "ACTIVE")
                    searchParams.set('documentExistence', "EXISTS")
                    searchParams.set('fromRelative', "PT48H")
                    setSearchParams(searchParams)
                    pageQueryStore.clear('sls')
                }}
                style={{marginTop: '0.3em'}}
            >
                <span className={`icon is-small`}>
                    <span className="material-symbols-outlined">filter_list_off</span>
                </span>
                <span style={{marginLeft: '0.3em'}}>Filter zurücksetzen</span>
            </button>
        </div>
    </div>
}

export {SlsItemFilterbar}
